/**
 * PreloaderScene — loads the global asset bundle behind a progress bar.
 *
 * Responsibilities (build-plan-v2 §2.2):
 *   - Pre-cache the assets the title flow + the first world scene need so
 *     the player never sees a blank canvas mid-transition.
 *   - Render a slim progress bar so a slow network shows movement instead
 *     of a frozen black screen.
 *   - Hand off to `TitleScene` on complete, or surface a load-error toast
 *     and continue (the failed asset's spot will fall back to `__MISSING`).
 *
 * Notes:
 *   - We deliberately re-list a few of the keys `OverworldScene.preload()`
 *     also loads. Phaser's loader is idempotent on cache hits — once a
 *     texture is in the cache, the second `this.load.atlas()` for the same
 *     key is a no-op. Loading the keys here means the world scene's preload
 *     finishes instantly and the player sees Verdana the moment Title fades.
 *   - No web-font work happens here: `main.ts` already awaits
 *     `document.fonts.ready` before booting Phaser.
 */

import * as Phaser from 'phaser';
import { OLD_GROVE_SHRINE_PHASER_KEY } from './painted/verdana/OldGroveShrineScene';
import {
  PRELOADER_SCENE_KEY,
} from './sceneKeys';
import { AmbientOverlayScene } from './painted/AmbientOverlayScene';
import { devWarn } from '../debug/devLog';

/** Width of the progress bar in px. */
const BAR_W = 320;
/** Height of the progress bar in px. */
const BAR_H = 14;
/** Inner padding between the bar's frame and its fill. */
const BAR_PAD = 2;

export class PreloaderScene extends Phaser.Scene {
  public static readonly KEY = PRELOADER_SCENE_KEY;

  constructor() {
    super({ key: PRELOADER_SCENE_KEY });
  }

  preload(): void {
    this.drawProgressUi();

    // Title-card art (Wave-1 generated, 1024×512). Loaded as an atlas because
    // the export pipeline emits a single-frame atlas alongside the PNG.
    this.load.atlas(
      'title-card',
      'assets/sprites/title-card/title-card.png',
      'assets/sprites/title-card/title-card.json',
    );

    // Pre-cache the world-scene atlases here so the Title→Overworld
    // transition is instant. OverworldScene.preload() will skip these on
    // cache-hit; the duplicate listing is intentional (see file header).
    this.load.atlas(
      'player',
      'assets/sprites/player/player.png',
      'assets/sprites/player/player.json',
    );
    this.load.atlas(
      'enemy-melee',
      'assets/sprites/enemy-melee/enemy-melee.png',
      'assets/sprites/enemy-melee/enemy-melee.json',
    );
    this.load.spritesheet(
      'overworld-iso',
      'assets/tilesets/overworld/overworld-iso.png',
      { frameWidth: 64, frameHeight: 32 },
    );
    this.load.json(
      'overworld-descriptor',
      'assets/tilesets/overworld/overworld-descriptor.json',
    );

    // AGE-1316 — Loading screen redesign art assets
    // 4 threshold background paintings (800×600 Ghibli landscapes)
    this.load.image('threshold-dawn', 'art/ui/threshold-dawn.png');
    this.load.image('threshold-morning', 'art/ui/threshold-morning.png');
    this.load.image('threshold-golden', 'art/ui/threshold-golden.png');
    this.load.image('threshold-settling', 'art/ui/threshold-settling.png');

    // 4 elemental vignette overlays (800×600 transparent PNGs)
    this.load.image('vignette-earth', 'art/ui/vignette-earth.png');
    this.load.image('vignette-wind', 'art/ui/vignette-wind.png');
    this.load.image('vignette-water', 'art/ui/vignette-water.png');
    this.load.image('vignette-ember', 'art/ui/vignette-ember.png');

    // 4 elemental wisp sprites (32×32 transparent PNGs)
    this.load.image('wisp-earth', 'art/ui/wisp-earth.png');
    this.load.image('wisp-air', 'art/ui/wisp-air.png');
    this.load.image('wisp-water', 'art/ui/wisp-water.png');
    this.load.image('wisp-fire', 'art/ui/wisp-fire.png');

    // AGE-1419 — painted-scene framework. PaintedScene reads this manifest
    // from cache to resolve per-scene specs, edge zones, and interactions.
    // Load failure is non-fatal — placeholder scenes can supply an inline
    // manifest fallback.
    this.load.json('journey-manifest', 'assets/scenes/journey-manifest.json');

    this.load.on('loaderror', (file: { key: string; url: string }) => {
      devWarn(`[PreloaderScene] asset load failed: ${file.key} (${file.url})`);
    });
  }

  create(): void {
    // AGE-1431 phase α — launch the persistent ambient overlay before any
    // gameplay scene starts. It runs in parallel above every scene that
    // follows and is never stopped, so the drift motes and grain are
    // already on screen the moment Title fades up.
    AmbientOverlayScene.ensureLaunched(this);

    // Tiny pause to make the progress bar visible even on instant local
    // hits — without it the bar would flash for one frame on a warm cache.
    // 80ms is below the perception-of-lag threshold so it never feels like
    // an artificial delay; it just gives the eye a beat.
    this.time.delayedCall(80, () => {
      this.scene.start(OLD_GROVE_SHRINE_PHASER_KEY);
    });
  }

  /**
   * Renders a centered progress bar that listens to the loader's `progress`
   * event. The bar is destroyed automatically when this scene transitions —
   * Phaser tears down owned game objects on shutdown.
   */
  private drawProgressUi(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    const frame = this.add.graphics();
    frame.lineStyle(2, 0xeae0c8, 1);
    frame.strokeRect(cx - BAR_W / 2, cy - BAR_H / 2, BAR_W, BAR_H);

    const fill = this.add.graphics();
    const innerW = BAR_W - BAR_PAD * 2;
    const innerH = BAR_H - BAR_PAD * 2;
    const innerX = cx - BAR_W / 2 + BAR_PAD;
    const innerY = cy - BAR_H / 2 + BAR_PAD;

    const label = this.add.text(cx, cy + BAR_H + 12, 'gathering threads…', {
      fontFamily: "'Almendra', serif",
      fontSize: '14px',
      color: '#eae0c8',
    });
    label.setOrigin(0.5, 0);

    this.load.on('progress', (pct: number) => {
      fill.clear();
      fill.fillStyle(0xeae0c8, 1);
      fill.fillRect(innerX, innerY, Math.max(0, innerW * pct), innerH);
    });
  }
}
